import { type Bone, type SkinnedMesh } from 'three'

export interface PoseBonePickerDeps {
  /** The fixed-position panel host. Built by `index.html`; the picker fills its body. */
  container: HTMLElement
  /** Called when the user clicks a bone row. The consumer routes this through PoseModeController.select_bone. */
  on_bone_picked: (bone: Bone) => void
}

/**
 * Floating panel for picking a bone in Pose Mode. Lists every bone in the active
 * skeleton with a search filter so users can target precise bones instead of
 * relying on viewport raycasting (which can miss small bones behind mesh).
 *
 * Visibility is driven by `show()` / `hide()` — the consumer calls these from
 * Pose Mode enter/exit. The panel surface itself is built by `index.html` so it
 * can be styled and (later) made draggable; we just populate its body.
 *
 * Bones are filtered to skip a few "structural" entries the user shouldn't
 * normally pick:
 *  - `root` (the top-level transform; pose-mode bone-pick already filters this)
 *  - bones whose name ends in `_end` (Three.js's "tail" virtual bones)
 */
export class PoseBonePicker {
  private readonly container: HTMLElement
  private readonly on_bone_picked: (bone: Bone) => void
  private readonly list_el: HTMLDivElement
  private readonly search_el: HTMLInputElement
  private readonly empty_state_el: HTMLDivElement

  private bones: Bone[] = []
  private filtered_bones: Bone[] = []
  private selected_bone: Bone | null = null
  private filter_text: string = ''

  constructor (deps: PoseBonePickerDeps) {
    this.container = deps.container
    this.on_bone_picked = deps.on_bone_picked

    // Build the inner DOM. Outer container + header come from index.html.
    this.container.innerHTML = `
      <div class="pose-bone-picker-header">
        <span class="pose-bone-picker-title">Bone picker</span>
      </div>
      <div class="pose-bone-picker-body">
        <input type="search" class="pose-bone-picker-search" placeholder="Filter bones…" aria-label="Filter bones by name" />
        <div class="pose-bone-picker-list" role="listbox" aria-label="Skeleton bones"></div>
        <div class="pose-bone-picker-empty" hidden>No bones match.</div>
      </div>
    `

    this.list_el = this.container.querySelector<HTMLDivElement>('.pose-bone-picker-list')!
    this.search_el = this.container.querySelector<HTMLInputElement>('.pose-bone-picker-search')!
    this.empty_state_el = this.container.querySelector<HTMLDivElement>('.pose-bone-picker-empty')!

    this.search_el.addEventListener('input', () => {
      this.filter_text = this.search_el.value.toLowerCase().trim()
      this.render()
    })

    // Delegated click — rows are recreated whenever the filter or skeleton changes.
    this.list_el.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null
      const row = target?.closest<HTMLElement>('.pose-bone-picker-row')
      if (row === null || row === undefined) return
      const bone_name = row.dataset.boneName
      if (bone_name === undefined) return
      const bone = this.bones.find((b) => b.name === bone_name)
      if (bone === undefined) return
      this.on_bone_picked(bone)
    })
  }

  /**
   * Populate the picker with every bone in the given mesh's skeleton. Pass `null`
   * to clear the list (e.g. when no model is loaded).
   */
  set_skeleton (skinned_mesh: SkinnedMesh | null): void {
    if (skinned_mesh === null) {
      this.bones = []
    } else {
      this.bones = skinned_mesh.skeleton.bones
        .filter((bone) => bone.name !== 'root' && !bone.name.endsWith('_end'))
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
    }
    this.render()
  }

  /**
   * Highlight the row corresponding to the currently-selected bone (so the user
   * can see which row matches the gizmo target). Pass `null` to clear.
   */
  set_selected_bone (bone: Bone | null): void {
    this.selected_bone = bone
    this.refresh_selection_highlight()
  }

  show (): void {
    this.container.hidden = false
  }

  hide (): void {
    this.container.hidden = true
  }

  is_visible (): boolean {
    return !this.container.hidden
  }

  // -- Internal -------------------------------------------------------------

  private render (): void {
    this.filtered_bones = this.filter_text === ''
      ? this.bones.slice()
      : this.bones.filter((bone) => bone.name.toLowerCase().includes(this.filter_text))

    this.empty_state_el.hidden = this.filtered_bones.length > 0

    const rows_html = this.filtered_bones.map((bone) => {
      const is_selected = this.selected_bone === bone
      const escaped = this.escape_html(bone.name)
      return `<button type="button" class="pose-bone-picker-row${is_selected ? ' selected' : ''}" data-bone-name="${escaped}" role="option" aria-selected="${is_selected ? 'true' : 'false'}">${escaped}</button>`
    }).join('')

    this.list_el.innerHTML = rows_html
  }

  private refresh_selection_highlight (): void {
    const rows = this.list_el.querySelectorAll<HTMLElement>('.pose-bone-picker-row')
    rows.forEach((row) => {
      const matches = row.dataset.boneName === this.selected_bone?.name
      row.classList.toggle('selected', matches)
      row.setAttribute('aria-selected', matches ? 'true' : 'false')
    })
  }

  private escape_html (value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }
}
